import React, { useEffect, useContext } from 'react';
import { useNavigate } from "react-router-dom";
import Axios from "axios";
import { useImmerReducer } from 'use-immer';
import { Grid, Typography, Button, TextField, Snackbar, Alert, FormHelperText, Box, useMediaQuery, useTheme } from "@mui/material";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useTranslation } from 'react-i18next';

import DispatchContext from "../../Contexts/DispatchContext";
import StateContext from "../../Contexts/StateContext";

function Register() {
    const { t, i18n } = useTranslation();
    const { config, environment } = require('../../index');
    const navigate = useNavigate();
    const GlobalDispatch = useContext(DispatchContext);
    const GlobalState = useContext(StateContext);
    const { executeRecaptcha } = useGoogleReCaptcha();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const initialState = {
        username: '',
        email: '',
        password: '',
        password2: '',
        sendRequest: 0,
        openSnack: false,
        disabledRegisterBtn: false,
        errors: {
            username: '',
            email: '',
            password: '',
            password2: '',
            server: ''
        },
        recaptchaToken: '',
    };

    function reducer(draft, action) {
        switch (action.type) {
            case 'setUsername':
                draft.username = action.value;
                draft.errors.username = '';
                draft.errors.server = '';
                break;
            case 'setEmail':
                draft.email = action.value;
                draft.errors.email = '';
                draft.errors.server = '';
                break;
            case 'setPassword':
                draft.password = action.value;
                draft.errors.password = '';
                draft.errors.server = '';
                break;
            case 'setPassword2':
                draft.password2 = action.value;
                draft.errors.password2 = (action.value !== draft.password) ? t('Administration.PasswordMatchError') : '';
                break;
            case 'setRecaptcha':
                draft.recaptchaToken = action.value;
                break;
            case 'disableBtn':
                draft.disabledRegisterBtn = true;
                break;
            case 'enableBtn':
                draft.disabledRegisterBtn = false;
                break;
            case 'setError':
                draft.errors[action.field] = action.message;
                break;
            case 'incrementSendRequest':
                draft.sendRequest++;
                break;
            case 'openSnack':
                draft.openSnack = true;
                break;
            default:
                break;
        }
    }

    const [state, dispatch] = useImmerReducer(reducer, initialState);

    // Client-side validation before submit
    const validateForm = () => {
        let valid = true;
        if (!state.username || state.username.length < 6 || !/^[a-zA-Z0-9]+$/.test(state.username)) {
            dispatch({ type: 'setError', field: 'username', message: t('Administration.UsernameError') });
            valid = false;
        }
        if (!state.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email)) {
            dispatch({ type: 'setError', field: 'email', message: t('Administration.EmailError') });
            valid = false;
        }
        if (!state.password || state.password.length < 8) {
            dispatch({ type: 'setError', field: 'password', message: t('Administration.PasswordError') });
            valid = false;
        }
        if (state.password !== state.password2) {
            dispatch({ type: 'setError', field: 'password2', message: t('Administration.PasswordMatchError') });
            valid = false;
        }
        return valid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm() || !executeRecaptcha) return;

        dispatch({ type: 'disableBtn' });

        try {
            const recaptchaToken = await executeRecaptcha("register");
            dispatch({ type: 'setRecaptcha', value: recaptchaToken });
            dispatch({ type: 'incrementSendRequest' });
        } catch (err) {
            console.error("reCAPTCHA failed:", err);
            dispatch({ type: 'setError', field: 'server', message: t('Administration.RecaptchaFailed') });
            dispatch({ type: 'enableBtn' });
        }
    };

    // Submit registration request
    useEffect(() => {
        if (!state.sendRequest) return;
        const source = Axios.CancelToken.source();

        const registerUser = async () => {
            try {
                const response = await Axios.post(
                    config[environment].registerEndpoint,
                    {
                        username: state.username,
                        email: state.email,
                        password: state.password,
                        re_password: state.password2,
                        recaptcha: state.recaptchaToken
                    },
                    { cancelToken: source.token }
                );

                dispatch({ type: 'openSnack' });
            } catch (err) {
                dispatch({ type: 'enableBtn' });
                const data = err.response?.data;
                if (err.response?.status === 500) {
                    dispatch({ type: 'setError', field: 'server', message: t('Administration.ServerError') });
                } else if (data) {
                    if (data.username) dispatch({ type: 'setError', field: 'username', message: t('Administration.UserExists') });
                    if (data.email) dispatch({ type: 'setError', field: 'email', message: t('Administration.EmailExists') });
                    if (data.password && data.password[0]) {
                        const msg = data.password[0];
                        if (msg.includes('similar')) dispatch({ type: 'setError', field: 'password', message: t('Administration.PasswordSimilarError') });
                        else if (msg.includes('common')) dispatch({ type: 'setError', field: 'password', message: t('Administration.PasswordCommonError') });
                        else if (msg.includes('numeric')) dispatch({ type: 'setError', field: 'password', message: t('Administration.PasswordNumericError') });
                    }
                }
            }
        };

        registerUser();
        return () => source.cancel();
    }, [state.sendRequest, state.username, state.email, state.password, state.password2, state.recaptchaToken, config, environment]);

    // Redirect after successful registration
    useEffect(() => {
        if (state.openSnack) {
            setTimeout(() => navigate(`/${i18n.language}/created`), 1500);
        }
    }, [state.openSnack, navigate, i18n.language]);

    return (
        <Box sx={{ 
            width: { xs: '90%', sm: '70%', md: '50%' }, 
            margin: { xs: "2rem auto", md: "3rem auto" },
            marginTop: { xs: '20px', md: '50px', lg: '100px' },
            border: "2px solid black", 
            padding: { xs: '1.5rem', md: '3rem' } 
            }}>
            <form onSubmit={handleSubmit}>
                <Grid container justifyContent="center">
                    <Typography variant={isMobile ? 'h5' : 'h4'}>{t('Administration.RegisterTitle')}</Typography>
                </Grid>

                {Object.values(state.errors).map((msg, idx) => msg ? <Alert key={idx} severity="error">{msg}</Alert> : null)}

                <Grid container sx={{ marginTop: "1rem" }}>
                    <TextField
                        id="username"
                        label={t('Administration.Username')}
                        aria-label="username"
                        variant="outlined"
                        fullWidth
                        value={state.username}
                        onChange={e => dispatch({ type: 'setUsername', value: e.target.value })}
                        error={!!state.errors.username}
                        helperText={state.errors.username}
                    />
                </Grid>

                <Grid container sx={{ marginTop: "1rem" }}>
                    <TextField
                        id="email"
                        label={t('Administration.Email')}
                        aria-label="email"
                        variant="outlined"
                        fullWidth
                        value={state.email}
                        onChange={e => dispatch({ type: 'setEmail', value: e.target.value })}
                        error={!!state.errors.email}
                        helperText={state.errors.email}
                    />
                </Grid>

                <Grid container sx={{ marginTop: "1rem" }}>
                    <TextField
                        id="password"
                        label={t('Administration.Password')}
                        aria-label="password"
                        variant="outlined"
                        type="password"
                        fullWidth
                        value={state.password}
                        onChange={e => dispatch({ type: 'setPassword', value: e.target.value })}
                        error={!!state.errors.password}
                        helperText={state.errors.password}
                    />
                </Grid>

                <Grid container sx={{ marginTop: "1rem" }}>
                    <TextField
                        id="password2"
                        label={t('Administration.ConfirmPassword')}
                        aria-label="confirm-password"
                        variant="outlined"
                        type="password"
                        fullWidth
                        value={state.password2}
                        onChange={e => dispatch({ type: 'setPassword2', value: e.target.value })}
                        error={!!state.errors.password2}
                    />
                    <FormHelperText sx={{ color: "red" }}>{state.errors.password2}</FormHelperText>
                </Grid>

                <Grid container sx={{ marginTop: "1rem" }}>
                    <Button variant='contained' fullWidth type='submit' disabled={state.disabledRegisterBtn}>{t('Administration.RegisterButton')}</Button>
                </Grid>
            </form>

            <Grid container justifyContent="center" sx={{ marginTop: "1rem" }}>
                <Typography variant='small'>
                    {t('Administration.AlreadyHaveAccount')}
                    <span onClick={() => navigate(`/${i18n.language}/login`)} style={{ cursor: "pointer", color: "green" }}> {t('Administration.SignInNow')}</span>
                </Typography>
            </Grid>

            <Snackbar
                open={state.openSnack}
                message={t('Administration.RegisterSuccess')}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                ContentProps={{ sx: { backgroundColor: 'green' } }}
            />
        </Box>
    );
}

export default Register;
